import { Directive, Input, OnDestroy, OnInit } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { ErrorAnchorService } from 'src/app/services/error-anchor.service'
import { ErrorField, PresentedError } from 'src/utils/presented-error'

/** Maps the error system's logical field names onto this form's control names. */
export type FieldErrorMap = Partial<Record<ErrorField, string>>

/**
 * Makes a form claim the backend validation failures that belong to its inputs, so they stop
 * flying off to a corner toast.
 *
 * The call site is unchanged — `handleArpioError` still funnels the error. This registers the
 * form with `ErrorAnchorService`, which offers it every error before the shell toast sees it;
 * a field-tier error whose `field` this form owns gets stamped onto that control and rendered
 * by `<arp-field-issue>`.
 *
 * ```html
 * <form [formGroup]="roleForm" [arpFieldErrorHost]="roleForm" [fieldErrorMap]="{ 'role-name': 'name' }">
 * ```
 */
@Directive({
  selector: '[arpFieldErrorHost]',
  standalone: false,
})
export class FieldErrorHostDirective implements OnInit, OnDestroy {
  @Input('arpFieldErrorHost') form: FormGroup

  /** Without a mapping the form claims nothing, so an unmapped error still reaches the user. */
  @Input() fieldErrorMap: FieldErrorMap = {}

  /** Distinguishes hosts in `ErrorAnchorService.registeredIds` when debugging. */
  @Input() fieldErrorHostId = 'form'

  private deregister: (() => void) | null = null

  constructor(private readonly anchors: ErrorAnchorService) {}

  ngOnInit(): void {
    this.deregister = this.anchors.register({
      id: this.fieldErrorHostId,
      accept: (presented) => this.claim(presented),
    })
  }

  ngOnDestroy(): void {
    this.deregister?.()
  }

  private claim(presented: PresentedError): boolean {
    if (presented.tier !== 'field' || !presented.field) return false

    const controlName = this.fieldErrorMap[presented.field]
    const control = controlName ? this.form?.get(controlName) : null
    if (!control) return false

    control.setErrors({ ...(control.errors ?? {}), server: { message: `${presented.title}. ${presented.detail}` } })
    control.markAsTouched()
    return true
  }
}
